import type { ConversationMessage } from './conversation';
import { responseAge, type ResponseWait } from './responseTime';

export interface InboxEntry {
  id: string;
  sourceId: string;
  threadId: string;
  subject: string;
  message: ConversationMessage;
  mine: boolean;
  unread: boolean;
}
export interface InboxThread {
  key: string;
  subject: string;
  entries: readonly InboxEntry[];
  latest: InboxEntry;
  mine: boolean;
  unreadCount: number;
  response?: ResponseWait;
}
export interface InboxFilter {
  mine: boolean;
  unread: boolean;
  waiting: boolean;
  minAgeMs: number;
}
export type InboxSort = 'recent' | 'responseAge';
export const DEFAULT_INBOX_FILTER: InboxFilter = { mine: false, unread: false, waiting: false, minAgeMs: 0 };
const instant = (value: string) => (Number.isFinite(Date.parse(value)) ? Date.parse(value) : 0);

export function groupInbox(entries: readonly InboxEntry[]): InboxThread[] {
  const groups = new Map<string, InboxEntry[]>();
  for (const entry of entries) {
    // Length-prefix avoids both cross-source thread collisions and delimiter collisions.
    const key = entry.sourceId.length + ':' + entry.sourceId + entry.threadId;
    const group = groups.get(key) ?? [];
    group.push(entry);
    groups.set(key, group);
  }
  return [...groups].map(([key, items]) => {
    const sorted = [...items].sort((a, b) => instant(a.message.sentAt) - instant(b.message.sentAt));
    const latest = sorted.at(-1)!;
    const waits = sorted.flatMap((item) => (item.message.response ? [item.message.response] : []));
    const response = waits.reduce<ResponseWait | undefined>(
      (oldest, wait) =>
        !oldest ||
        (Number.isFinite(Date.parse(wait.since)) &&
          (!Number.isFinite(Date.parse(oldest.since)) || Date.parse(wait.since) < Date.parse(oldest.since)))
          ? wait
          : oldest,
      undefined,
    );
    return {
      key,
      entries: sorted,
      latest,
      subject: latest.subject,
      mine: sorted.some((item) => item.mine),
      unreadCount: sorted.filter((item) => item.unread).length,
      response,
    };
  });
}
export function selectInbox(
  groups: readonly InboxThread[],
  filter: InboxFilter,
  sort: InboxSort,
  now: number,
) {
  return groups
    .filter(
      (group) =>
        (!filter.mine || group.mine) &&
        (!filter.unread || group.unreadCount > 0) &&
        (!filter.waiting || !!group.response) &&
        (!(filter.minAgeMs > 0) ||
          (group.response && (responseAge(group.response.since, now) ?? -1) >= filter.minAgeMs)),
    )
    .sort((a, b) => {
      if (sort === 'responseAge') {
        const aAge = a.response ? responseAge(a.response.since, now) : null;
        const bAge = b.response ? responseAge(b.response.since, now) : null;
        if (aAge !== bAge) return (bAge ?? -1) - (aAge ?? -1);
      }
      return (
        instant(b.latest.message.sentAt) - instant(a.latest.message.sentAt) || a.key.localeCompare(b.key)
      );
    });
}
